// src/utils/securityMonitoring.js
import { addDoc, collection, serverTimestamp, setDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

// Security monitoring configuration
const SECURITY_CONFIG = {
  SUSPICIOUS_ACTIVITIES: {
    MULTIPLE_FAILED_LOGINS: 5,
    RAPID_PURCHASES: 3,
    LARGE_AMOUNT_PURCHASES: 1000,
    UNUSUAL_LOCATION_LOGIN: true,
    RAPID_GAME_CREATION: 10,
    SUSPICIOUS_IP_PATTERNS: ['tor', 'vpn', 'proxy']
  },
  
  ALERT_THRESHOLDS: {
    FAILED_LOGIN_ATTEMPTS: 3,
    PURCHASE_FREQUENCY: 5, // per hour
    GAME_CREATION_FREQUENCY: 5,   // per hour
    LARGE_TRANSACTION_AMOUNT: 500
  }
};

// In-memory tracking for rate limiting
const securityTracking = new Map();

/**
 * Log security event
 * @param {string} eventType - Type of security event
 * @param {Object} eventData - Event data
 * @param {string} userId - User ID (optional)
 * @param {string} severity - Event severity (low, medium, high, critical)
 */
export const logSecurityEvent = async (eventType, eventData, userId = null, severity = 'medium') => {
  try {
    const securityEvent = {
      eventType,
      eventData,
      userId,
      severity,
      timestamp: serverTimestamp(),
      ipAddress: eventData.ipAddress || 'unknown',
      userAgent: eventData.userAgent || 'unknown',
      sessionId: eventData.sessionId || 'unknown'
    };

    // Create a unique ID for the security event to prevent conflicts
    const eventId = `${eventType}_${userId || 'anonymous'}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    await setDoc(doc(db, 'securityEvents', eventId), securityEvent);

    // Log to console for development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔒 Security Event [${severity.toUpperCase()}]:`, {
        type: eventType,
        userId,
        data: eventData
      });
    }

    // Trigger alerts for high severity events
    if (severity === 'high' || severity === 'critical') {
      await triggerSecurityAlert(securityEvent);
    }

  } catch (error) {
    console.error('Error logging security event:', error);
    // Don't throw the error - just log it and continue
    // This prevents security logging from breaking the login flow
  }
};

/**
 * Monitor login attempts
 * @param {string} userId - User ID
 * @param {boolean} success - Whether login was successful
 * @param {Object} loginData - Login attempt data
 */
export const monitorLoginAttempt = async (userId, success, loginData) => {
  try {
    const userKey = `login_${userId}`;
    const userAttempts = securityTracking.get(userKey) || [];
    const now = Date.now();
    
    // Add current attempt
    userAttempts.push({
      timestamp: now,
      success,
      ipAddress: loginData.ipAddress,
      userAgent: loginData.userAgent
    });
    
    // Keep only last 10 attempts
    const recentAttempts = userAttempts.slice(-10);
    securityTracking.set(userKey, recentAttempts);
    
    // Check for suspicious patterns
    const failedAttempts = recentAttempts.filter(attempt => !attempt.success);
    
    if (failedAttempts.length >= SECURITY_CONFIG.ALERT_THRESHOLDS.FAILED_LOGIN_ATTEMPTS) {
      await logSecurityEvent('multiple_failed_logins', {
        userId,
        failedAttempts: failedAttempts.length,
        recentAttempts: recentAttempts.length,
        ipAddress: loginData.ipAddress,
        userAgent: loginData.userAgent
      }, userId, 'high');
    }
    
    // Check for unusual location
    if (loginData.location && loginData.lastKnownLocation) {
      const locationChange = detectLocationAnomaly(loginData.location, loginData.lastKnownLocation);
      if (locationChange.suspicious) {
        await logSecurityEvent('unusual_location_login', {
          userId,
          currentLocation: loginData.location,
          lastKnownLocation: loginData.lastKnownLocation,
          distance: locationChange.distance,
          timeSinceLastLogin: locationChange.timeSinceLastLogin
        }, userId, 'medium');
      }
    }
    
  } catch (error) {
    console.error('Error monitoring login attempt:', error);
  }
};

/**
 * Monitor payments for suspicious activity
 * @param {string} userId - User ID
 * @param {number} amount - Purchase amount
 * @param {Object} purchaseData - Purchase data
 */
export const monitorPayment = async (userId, amount, purchaseData) => {
  try {
    const userKey = `purchase_${userId}`;
    const userPurchases = securityTracking.get(userKey) || [];
    const now = Date.now();
    
    // Add current purchase
    userPurchases.push({
      timestamp: now,
      amount,
      paymentMethod: purchaseData.paymentMethod
    });
    
    // Keep only purchases from last 24 hours
    const recentPurchases = userPurchases.filter(
      purchase => now - purchase.timestamp < 24 * 60 * 60 * 1000
    );
    securityTracking.set(userKey, recentPurchases);
    
    // Check for rapid purchases
    const hourlyPurchases = recentPurchases.filter(
      purchase => now - purchase.timestamp < 60 * 60 * 1000
    );
    
    if (hourlyPurchases.length >= SECURITY_CONFIG.ALERT_THRESHOLDS.PURCHASE_FREQUENCY) {
      await logSecurityEvent('rapid_purchases', {
        userId,
        purchasesInLastHour: hourlyPurchases.length,
        totalAmount: hourlyPurchases.reduce((sum, p) => sum + p.amount, 0),
        paymentMethods: [...new Set(hourlyPurchases.map(p => p.paymentMethod))]
      }, userId, 'high');
    }
    
    // Check for large amount purchases
    if (amount >= SECURITY_CONFIG.ALERT_THRESHOLDS.LARGE_TRANSACTION_AMOUNT) {
      await logSecurityEvent('large_purchase', {
        userId,
        amount,
        paymentMethod: purchaseData.paymentMethod,
        userHistory: {
          totalPurchases: recentPurchases.length,
          totalSpent: recentPurchases.reduce((sum, p) => sum + p.amount, 0)
        }
      }, userId, 'medium');
    }
    
  } catch (error) {
    console.error('Error monitoring payment:', error);
  }
};

/**
 * Monitor game creation for spam/abuse
 * @param {string} userId - User ID
 * @param {Object} gameData - Game data
 */
export const monitorGameCreation = async (userId, gameData) => {
  try {
    const userKey = `game_creation_${userId}`;
    const userGames = securityTracking.get(userKey) || [];
    const now = Date.now();
    
    // Add current game creation
    userGames.push({
      timestamp: now,
      gameTitle: gameData.title,
      gameSize: JSON.stringify(gameData).length
    });
    
    // Keep only creations from last 24 hours
    const recentCreations = userGames.filter(
      creation => now - creation.timestamp < 24 * 60 * 60 * 1000
    );
    securityTracking.set(userKey, recentCreations);
    
    // Check for rapid game creation
    const hourlyCreations = recentCreations.filter(
      creation => now - creation.timestamp < 60 * 60 * 1000
    );
    
    if (hourlyCreations.length >= SECURITY_CONFIG.ALERT_THRESHOLDS.GAME_CREATION_FREQUENCY) {
      await logSecurityEvent('rapid_game_creation', {
        userId,
        creationsInLastHour: hourlyCreations.length,
        gameTitles: hourlyCreations.map(c => c.gameTitle),
        averageGameSize: hourlyCreations.reduce((sum, c) => sum + c.gameSize, 0) / hourlyCreations.length
      }, userId, 'medium');
    }
    
    // Check for suspicious game content
    const contentAnalysis = analyzeGameContent(gameData);
    if (contentAnalysis.suspicious) {
      await logSecurityEvent('suspicious_game_content', {
        userId,
        gameTitle: gameData.title,
        suspiciousPatterns: contentAnalysis.patterns,
        contentLength: JSON.stringify(gameData).length
      }, userId, 'high');
    }
    
  } catch (error) {
    console.error('Error monitoring game creation:', error);
  }
};

/**
 * Detect location anomalies
 * @param {Object} currentLocation - Current login location
 * @param {Object} lastKnownLocation - Last known location
 * @returns {Object} Location analysis
 */
const detectLocationAnomaly = (currentLocation, lastKnownLocation) => {
  try {
    const distance = calculateDistance(
      currentLocation.lat, currentLocation.lng,
      lastKnownLocation.lat, lastKnownLocation.lng
    );
    
    const timeSinceLastLogin = Date.now() - lastKnownLocation.timestamp;
    const hoursSinceLastLogin = timeSinceLastLogin / (1000 * 60 * 60);
    
    // Suspicious if: distance > 500km and time < 2 hours
    const suspicious = distance > 500 && hoursSinceLastLogin < 2;
    
    return {
      suspicious,
      distance,
      timeSinceLastLogin: hoursSinceLastLogin
    };
    
  } catch (error) {
    console.error('Error detecting location anomaly:', error);
    return { suspicious: false, distance: 0, timeSinceLastLogin: 0 };
  }
};

/**
 * Calculate distance between two points (Haversine formula)
 * @param {number} lat1 - Latitude 1
 * @param {number} lon1 - Longitude 1
 * @param {number} lat2 - Latitude 2
 * @param {number} lon2 - Longitude 2
 * @returns {number} Distance in kilometers
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Analyze game content for suspicious patterns
 * @param {Object} gameData - Game data to analyze
 * @returns {Object} Content analysis result
 */
const analyzeGameContent = (gameData) => {
  try {
    const patterns = [];
    
    // Check for repetitive content
    if (gameData.questions && Array.isArray(gameData.questions)) {
      const questionTexts = gameData.questions.map(q => q.text.toLowerCase());
      const uniqueQuestions = new Set(questionTexts);
      
      if (questionTexts.length > uniqueQuestions.size * 1.5) {
        patterns.push('repetitive_questions');
      }
      
      // Check for very short questions
      const shortQuestions = questionTexts.filter(text => text.length < 10);
      if (shortQuestions.length > questionTexts.length * 0.3) {
        patterns.push('many_short_questions');
      }
    }
    
    // Check for suspicious characters or patterns
    const contentString = JSON.stringify(gameData);
    if (contentString.includes('script') || contentString.includes('javascript')) {
      patterns.push('suspicious_script_tags');
    }
    
    // Check for excessive length
    if (contentString.length > 50000) {
      patterns.push('excessive_content_length');
    }
    
    return {
      suspicious: patterns.length > 0,
      patterns
    };
    
  } catch (error) {
    console.error('Error analyzing game content:', error);
    return { suspicious: false, patterns: [] };
  }
};

/**
 * Trigger security alert for high-priority events
 * @param {Object} securityEvent - Security event data
 */
const triggerSecurityAlert = async (securityEvent) => {
  try {
    // Create a unique ID for the alert to prevent conflicts
    const alertId = `${securityEvent.eventType}_${securityEvent.userId}_${Date.now()}`;
    
    // Log to admin alerts collection with unique ID
    await setDoc(doc(db, 'adminAlerts', alertId), {
      ...securityEvent,
      status: 'pending',
      createdAt: serverTimestamp(),
      alertId: alertId
    });
    
    // In a production environment, you might also:
    // - Send email alerts to admins
    // - Send SMS notifications
    // - Trigger automated responses
    // - Log to external security monitoring systems
    
  } catch (error) {
    console.error('Error triggering security alert:', error);
  }
};

/**
 * Get user's security status
 * @param {string} userId - User ID
 * @returns {Object} Security status
 */
export const getUserSecurityStatus = (userId) => {
  try {
    const loginKey = `login_${userId}`;
    const purchaseKey = `purchase_${userId}`;
    const gameKey = `game_creation_${userId}`;
    
    const loginAttempts = securityTracking.get(loginKey) || [];
    const purchases = securityTracking.get(purchaseKey) || [];
    const gameCreations = securityTracking.get(gameKey) || [];
    
    const now = Date.now();
    const last24Hours = 24 * 60 * 60 * 1000;
    
    return {
      failedLogins24h: loginAttempts.filter(a => !a.success && now - a.timestamp < last24Hours).length,
      purchases24h: purchases.filter(p => now - p.timestamp < last24Hours).length,
      gameCreations24h: gameCreations.filter(g => now - g.timestamp < last24Hours).length,
      totalSpent24h: purchases
        .filter(p => now - p.timestamp < last24Hours)
        .reduce((sum, p) => sum + p.amount, 0),
      lastActivity: Math.max(
        ...loginAttempts.map(a => a.timestamp),
        ...purchases.map(p => p.timestamp),
        ...gameCreations.map(g => g.timestamp)
      )
    };
    
  } catch (error) {
    console.error('Error getting user security status:', error);
    return {
      failedLogins24h: 0,
      purchases24h: 0,
      gameCreations24h: 0,
      totalSpent24h: 0,
      lastActivity: 0
    };
  }
}; 